import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { setPageMeta, setJsonLd, SITE_BASE } from "@/lib/api";
import { Reveal } from "@/components/Motion";

export const TOOL_LIST = [
  ["pace-calculator", "Running Pace Calculator", "Distance plus time equals your pace per mile and per kilometer."],
  ["race-predictor", "Race Time Predictor", "One recent result predicts your 5K through marathon times."],
  ["pace-converter", "Pace Converter", "Min/mi, min/km, and mph, translated instantly."],
  ["training-zones", "Training Zone Calculator", "Turn a 5K time into five usable training paces."],
  ["heart-rate-zones", "Heart Rate Zone Calculator", "Five HR zones from your age, tighter with resting HR."],
  ["calories-burned", "Running Calorie Calculator", "What that run actually cost, in honest numbers."],
  ["split-calculator", "Race Split Calculator", "Your goal time as mile-by-mile splits, three strategies."],
  ["race-fueling", "Race Fueling & BMI Calculator", "Carbs, fluid, sodium, and gels for your race distance."],
  ["hyrox-split-calculator", "HYROX Split Calculator", "Your target finish time, broken into eight run splits."],
  ["mileage-progression", "Weekly Mileage Calculator", "A safe week-by-week build to your target volume."],
  ["club-finder-quiz", "Run Club Finder Quiz", "Four questions. The kind of club you should look for."],
  ["runner-tdee-calculator", "Runner TDEE Calculator", "Daily calories from your real mileage, not a vague activity level."],
  ["runner-macro-calculator", "Runner Macro Calculator", "Carbs, protein and fat in grams, scaled to your training week."],
  ["runner-protein-calculator", "Runner Protein Calculator", "Your daily protein target and how to split it across meals."],
  ["race-fueling-planner", "Race Fueling Planner", "A complete carb, fluid and sodium plan for your race."],
  ["hyrox-finish-time", "HYROX Finish Time Predictor", "Predict your HYROX finish from run pace and station strength."],
  ["recovery-readiness-score", "Recovery Readiness Score", "Score today's readiness from sleep, soreness, and stress."],
  ["runner-longevity-score", "Runner Longevity Score", "How your running habits stack up for the long game."],
  ["body-composition-calculator", "Body Composition Calculator", "Body fat and lean mass estimates from tape measurements."],
  ["body-recomp-calculator", "Body Recomp Calculator", "Lose fat and hold muscle: targets for runners who lift."],
  ["one-rep-max-calculator", "One Rep Max Calculator", "Estimate your 1RM and training percentages from any set."],
];

export default function ToolsIndex() {
  useEffect(() => {
    setPageMeta("Free Running Calculators and Tools | Ninja Run Club", "Pace calculator, race predictor, HYROX split calculator, training zones, mileage progression, and a run club finder quiz. Free, no signup.");
    return setJsonLd("tools-jsonld", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Free running calculators and tools",
      numberOfItems: TOOL_LIST.length,
      itemListElement: TOOL_LIST.map(([slug, name], i) => ({
        "@type": "ListItem",
        position: i + 1,
        name,
        item: `${SITE_BASE}/tools/${slug}`,
      })),
    });
  }, []);

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-16 pb-10 border-b border-white/10">
        <Reveal>
          <p className="font-mono-label text-white/40">THE TOOLKIT</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">DO THE MATH.</h1>
          <p className="text-white/50 text-sm max-w-2xl mt-4">
            Twenty-one free running calculators covering pacing, race prediction, fueling, heart-rate zones, HYROX splits,
            and recovery — no signup, no paywall, assumptions stated on every tool. Run the numbers, email yourself the
            result, then go do the actual work.
          </p>
        </Reveal>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border-b border-white/10" data-testid="tools-grid">
        {TOOL_LIST.map(([slug, name, desc], i) => (
          <Link key={slug} to={`/tools/${slug}`} data-testid={`tool-card-${slug}`} className="group bg-night p-8 lg:p-12 hover:bg-white hover:text-ink transition-colors duration-300">
            <div className="flex items-start justify-between">
              <span className="font-mono-label text-white/30 group-hover:text-ink/40">{String(i + 1).padStart(2, "0")}</span>
              <ArrowUpRight size={18} className="opacity-30 group-hover:opacity-100 transition-opacity duration-200" />
            </div>
            <h2 className="font-display text-2xl lg:text-3xl mt-8 leading-tight">{name}</h2>
            <p className="text-sm text-white/50 group-hover:text-ink/60 mt-3">{desc}</p>
          </Link>
        ))}
      </div>

      <Link to="/plan" data-testid="tools-plan-banner" className="group mt-10 border border-white/15 p-8 lg:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 hover:bg-white hover:text-ink transition-colors duration-300 block">
        <div>
          <p className="font-mono-label opacity-50">BEYOND THE CALCULATORS</p>
          <p className="font-display text-2xl lg:text-4xl mt-2">BUILD A FULL TRAINING BLOCK.</p>
          <p className="text-sm opacity-60 mt-2">Five questions, a week-by-week plan for your race. Free.</p>
        </div>
        <ArrowUpRight size={20} className="opacity-40 group-hover:opacity-100 shrink-0" />
      </Link>
    </div>
  );
}
